import { Trans } from '@lingui/macro'
import Circle from 'assets/images/white-loader.svg'
import { ButtonEmpty, ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import { RowBetween } from 'components/Row'
import { useSportContract } from 'hooks/useContract'
import { transparentize } from 'polished'
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import NumberFormat from 'react-number-format'
import { useDarkModeManager } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { CustomLightSpinner, ThemedText } from 'theme'
import { tokenToDecimals } from 'utils/tokens'
import useInterval from 'utils/useInterval'
import { isMobile } from 'utils/userAgent'

import { calculateRatio } from './Events'

const Wrapper = styled.div`
  width: 100%;
  padding: 16px;
  margin: auto;
`

const StyledPositionCard = styled(LightCard)<{ bgColor: any }>`
  border: none;
  background: ${({ theme, bgColor }) =>
    `radial-gradient(91.85% 100% at 1.84% 0%, ${transparentize(0.8, bgColor)} 20%, ${theme.bg2} 100%) `};
  position: relative;
  overflow: hidden;
  margin-bottom: 1rem;
`

const ResponsiveGrid = styled.div<{ isMobile?: boolean }>`
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr 0.5fr 0.5fr' : '1fr 2fr 2fr 1.2fr 1fr 1fr')};
  padding: 8px;
`

interface IAcornSportBetGameProvider {
  id: string
  sportType: string
  leagueName: string
}

interface IAcornSportBetGame {
  gameIndex: string
  provider: IAcornSportBetGameProvider
  homeTeam: string
  awayTeam: string
  homeScore?: string
  awayScore?: string
  statusId?: string
  volumen?: string
  volumenHome?: string
  volumenAway?: string
  volumenDraw?: string
}

interface IAcornSportBet {
  id: string
  betIndex: string
  player: string
  amount: string
  game: IAcornSportBetGame
  betPosition: string
  date: string
  claimed?: boolean
}

interface RowBetsProps {
  item: IAcornSportBet
  getBets: () => Promise<void>
}

function RowBets({ item, getBets }: RowBetsProps) {
  const [isDark] = useDarkModeManager()
  const [loading, setLoading] = useState(false)

  const sportContract = useSportContract()

  const [showMore, setShowMore] = useState(false)

  const evaluateWin = (item: IAcornSportBet) => {
    if (['1', '2', '15'].includes(item.game.statusId || '')) {
      return true
    }
    if (item.betPosition === '0' && Number(item.game.homeScore) > Number(item.game.awayScore)) {
      return true
    }
    if (item.betPosition === '1' && Number(item.game.homeScore) < Number(item.game.awayScore)) {
      return true
    }
    if (item.betPosition === '2' && Number(item.game.homeScore) === Number(item.game.awayScore)) {
      return true
    }

    return false
  }

  const claimBet = async (_provider: string, _gameIndex: string, _betIndex: string) => {
    setLoading(true)
    try {
      const response = await sportContract?.claimGame(_provider, Number(_gameIndex), _betIndex)
      await response?.wait()
      await getBets()
      setLoading(false)
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  }

  useInterval(getBets, 10000)

  return (
    <StyledPositionCard bgColor={isDark ? '#4D8FEA' : '#4D8F67'}>
      <ResponsiveGrid isMobile={isMobile}>
        {!isMobile && <ThemedText.Body>{new Date(Number(item.date) * 1000).toLocaleString()}</ThemedText.Body>}
        <AutoColumn>
          <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
            <img
              src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${item.game.provider.sportType}/${item.game.homeTeam}_Home.svg`}
              alt=""
              width={isMobile ? '12px' : '14px'}
              style={{ marginRight: '0.2rem' }}
            />
            {item.game.homeTeam}
          </ThemedText.Body>
          <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
            <img
              src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${item.game.provider.sportType}/${item.game.awayTeam}_Away.svg`}
              alt=""
              width={isMobile ? '12px' : '14px'}
              style={{ marginRight: '0.2rem' }}
            />
            {item.game.awayTeam}
          </ThemedText.Body>
        </AutoColumn>
        {!isMobile && (
          <ThemedText.Body>
            {Number(item.betPosition) === 0 ? (
              item.game.homeTeam
            ) : Number(item.betPosition) === 1 ? (
              item.game.awayTeam
            ) : (
              <Trans>Draw</Trans>
            )}
          </ThemedText.Body>
        )}
        {!isMobile && (
          <ThemedText.Body>
            <NumberFormat
              value={tokenToDecimals(item.amount, 18)}
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </ThemedText.Body>
        )}
        {!isMobile && (
          <ThemedText.Body>
            {['8', '11'].includes(item.game.statusId || '') ? (
              <span>
                {item.betPosition === '0' && Number(item.game.homeScore) > Number(item.game.awayScore) ? (
                  <span>
                    {tokenToDecimals(
                      (
                        Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenHome || '0')
                      ).toString(),
                      18
                    ).toFixed(2)}{' '}
                  </span>
                ) : item.betPosition === '1' && Number(item.game.homeScore) < Number(item.game.awayScore) ? (
                  <span>
                    {tokenToDecimals(
                      (
                        Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenAway || '0')
                      ).toString(),
                      18
                    ).toFixed(2)}{' '}
                  </span>
                ) : item.betPosition === '2' && Number(item.game.homeScore) === Number(item.game.awayScore) ? (
                  <span>
                    {tokenToDecimals(
                      (
                        Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenDraw || '0')
                      ).toString(),
                      18
                    ).toFixed(2)}{' '}
                  </span>
                ) : (
                  <span>0</span>
                )}
              </span>
            ) : (
              <span>
                <Trans>Pending</Trans>
              </span>
            )}
          </ThemedText.Body>
        )}
        {['1', '2', '8', '11', '15'].includes(item.game.statusId || '') ? (
          <span>
            {evaluateWin(item) ? (
              !item.claimed ? (
                loading ? (
                  <>
                    <Trans>Confirming</Trans>{' '}
                    <CustomLightSpinner
                      src={Circle}
                      alt="loader"
                      size={'18px'}
                      style={{ marginLeft: '0.5em', textDecoration: 'bold' }}
                    />{' '}
                  </>
                ) : (
                  <ButtonPrimary
                    onClick={() => claimBet(item.game.provider.id, item.game.gameIndex, item.betIndex)}
                    size="10px"
                    fontSize={isMobile ? '12px' : '16px'}
                  >
                    {['1', '2', '15'].includes(item.game.statusId || '') ? <Trans>Refund</Trans> : <Trans>Claim</Trans>}
                  </ButtonPrimary>
                )
              ) : (
                <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
                  <Trans>Winner</Trans>
                </ThemedText.Body>
              )
            ) : (
              <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
                <Trans>Loss</Trans>
              </ThemedText.Body>
            )}
          </span>
        ) : (
          <span>
            <Trans>Pending</Trans>
          </span>
        )}
        {isMobile && (
          <ButtonEmpty padding="6px 8px" $borderRadius="12px" width="100%" onClick={() => setShowMore(!showMore)}>
            {showMore ? (
              <>
                <ChevronUp size="20" style={{ marginLeft: '8px', height: '20px', minWidth: '20px' }} />
              </>
            ) : (
              <>
                <ChevronDown size="20" style={{ marginLeft: '8px', height: '20px', minWidth: '20px' }} />
              </>
            )}
          </ButtonEmpty>
        )}
      </ResponsiveGrid>
      {isMobile && showMore && (
        <Wrapper>
          <RowBetween>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>Date:</ThemedText.Body>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              {new Date(Number(item.date) * 1000).toLocaleString()}
            </ThemedText.Body>
          </RowBetween>
          <RowBetween>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              <Trans>Bet</Trans>:
            </ThemedText.Body>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              {Number(item.betPosition) === 0 ? (
                item.game.homeTeam
              ) : Number(item.betPosition) === 1 ? (
                item.game.awayTeam
              ) : (
                <Trans>Draw</Trans>
              )}
            </ThemedText.Body>
          </RowBetween>
          <RowBetween>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              <Trans>Amount</Trans>:
            </ThemedText.Body>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              <NumberFormat
                value={tokenToDecimals(item.amount, 18)}
                decimalScale={2}
                fixedDecimalScale
                displayType="text"
              />
            </ThemedText.Body>
          </RowBetween>
          <RowBetween>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              <Trans>Prize</Trans>:
            </ThemedText.Body>
            <ThemedText.Body fontSize={isMobile ? '12px' : '16px'}>
              {['8', '11'].includes(item.game.statusId || '') ? (
                <span>
                  {item.betPosition === '0' && Number(item.game.homeScore) > Number(item.game.awayScore) ? (
                    <span>
                      {tokenToDecimals(
                        (
                          Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenHome || '0')
                        ).toString(),
                        18
                      ).toFixed(2)}{' '}
                    </span>
                  ) : item.betPosition === '1' && Number(item.game.homeScore) < Number(item.game.awayScore) ? (
                    <span>
                      {tokenToDecimals(
                        (
                          Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenAway || '0')
                        ).toString(),
                        18
                      ).toFixed(2)}{' '}
                    </span>
                  ) : item.betPosition === '2' && Number(item.game.homeScore) === Number(item.game.awayScore) ? (
                    <span>
                      {tokenToDecimals(
                        (
                          Number(item.amount) * calculateRatio(item.game.volumen || '0', item.game.volumenDraw || '0')
                        ).toString(),
                        18
                      ).toFixed(2)}{' '}
                    </span>
                  ) : (
                    <span>0</span>
                  )}
                </span>
              ) : (
                <span>
                  <Trans>Pending</Trans>
                </span>
              )}
            </ThemedText.Body>
          </RowBetween>
        </Wrapper>
      )}
    </StyledPositionCard>
  )
}

export default RowBets
